import { useState, useRef, useCallback, useEffect } from "react";
import { Button, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIos";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import styled, { css } from "styled-components/macro";
import { areArraysEqual, HubletTag } from "../supervisorContextUtils";
import type { AllocationObj } from "types/types";
import useContainerDimensions from "utils/useContainerDimensions";

const ICON_DIMENSIONS = 50;

const TagsGridStyles = styled.div<{
  $numColumns: number;
  $numRows: number;
  $isMobileOrSmaller: boolean;
}>`
  .backButton {
    margin-left: 1em;
    justify-content: left;
    width: 2em;
  }

  .tagsGrid {
    background: black;
    padding: 1em;
    display: grid;

    /* Order of tags given by priority are in order of each column */
    grid-auto-flow: column;
    /* num rows and columns depends on how many tags in the tags config */
    grid-template-rows: repeat(${props => props.$numRows}, 1fr);
    grid-template-columns: repeat(${props => props.$numColumns}, 1fr);

    /* Make columns uniform */
    grid-auto-columns: 1fr;
    grid-gap: 5px;

    button {
      font-family: inherit;
      text-transform: inherit;
    }
    .MuiButton-text,
    .MuiButton-root {
      padding: 0;
    }

    .tag {
      display: grid;
      grid-template-rows: 1fr 1fr auto;
      align-items: center;

      .hasMore {
        justify-self: right;
      }
    }

    .tagName {
      word-wrap: break-word;
      white-space: normal;
      max-height: 76px;
      overflow: hidden;
      &.isVeryLong {
        font-size: 0.8em;
      }
    }

    .tagInputRow {
      display: flex;
    }
  }
  ${({ $isMobileOrSmaller }) =>
    $isMobileOrSmaller &&
    css`
      .tagsGrid {
        .tagName {
          word-break: break-all;
          width: 100%;
          padding: 0.5em;
          overflow-wrap: anywhere;
          box-sizing: border-box;
          line-height: 1.2em;
        }
      }
    `}
`;

/**
 * Stolen from portal tag preview (which displays what hublet displays)
 * determine number of rows and columns there should be based on number of tags
 *
 * Rows:
 * [0, 2] -> 1
 * (2, 6] -> 2
 * (6, infinity] -> 3
 *
 * Columns:
 * [0, 4] -> 2
 * (4, 9] -> 3
 * (9, 12] -> 4
 * (12, infinity] -> 5
 *
 * @param {number} numTags
 * @returns {number}
 */
const getNumberOfRowsAndCols = (
  numTags: number
): { rows: number; columns: number } => {
  const rows = numTags <= 2 ? 1 : numTags <= 6 ? 2 : 3;
  const columns = numTags <= 4 ? 2 : numTags <= 9 ? 3 : numTags <= 12 ? 4 : 5;

  return { rows, columns };
};

type TagsGridProps = {
  formState?: AllocationObj[] | any[];
  handleFinalTagClick: (tag: HubletTag) => void;
  hubletTags: HubletTag[];
  CustomBackButtonComponent?: (props: any) => React.JSX.Element;
};

export default function TagsGrid({
  formState,
  handleFinalTagClick,
  hubletTags,
  CustomBackButtonComponent,
}: TagsGridProps) {
  const [tagList, setTagList] = useState(hubletTags);
  const [shouldShowBackButton, setShouldShowBackButton] = useState(false);
  // used to keep track of previous tag list for back button
  const previousTagList = useRef([] as any[]);

  const handleTagClick = useCallback(
    subTagList => {
      // add current tag list to previousTagList
      previousTagList.current = [...previousTagList.current, tagList];
      setTagList(subTagList);
      setShouldShowBackButton(true);
    },
    [tagList]
  );

  const handleBackClick = useCallback(() => {
    // get the previous tag list
    const oneLevelUpTagList =
      previousTagList.current[previousTagList.current.length - 1];
    // set previous tag list as current tag list (go up a level)
    setTagList(oneLevelUpTagList);
    // remove oneLevelUpTagList from history array
    previousTagList.current.pop();
    // should only show back button if we aren't on principle level
    setShouldShowBackButton(!areArraysEqual(oneLevelUpTagList, hubletTags));
  }, [hubletTags]);

  const { rows, columns } = getNumberOfRowsAndCols(tagList.length);

  const [ref, dimensions] = useContainerDimensions();
  const BackButton = CustomBackButtonComponent || IconButton;

  // to avoid twitchy styles, only detect smaller breakpoint once
  const [isMobileOrSmaller, setIsMobileOrSmaller] = useState(false);
  useEffect(() => {
    if (dimensions.width && dimensions.width < 500) {
      setIsMobileOrSmaller(true);
    }
  }, [dimensions, setIsMobileOrSmaller]);
  return (
    <TagsGridStyles
      ref={ref}
      $numRows={rows}
      $numColumns={columns}
      $isMobileOrSmaller={isMobileOrSmaller}
    >
      {shouldShowBackButton && (
        <BackButton onClick={handleBackClick} style={{ marginLeft: "1em" }}>
          <ArrowBackIcon />
        </BackButton>
      )}
      <div className="tagsGrid">
        {tagList.map(tag => {
          const { tagName, subTags, icon, textColor, color } = tag;

          // Convert svg icon string to base64data for display in img tag
          const base64data = btoa(icon ?? "");

          const hasBeenSelectedAlready =
            formState &&
            formState.findIndex(x => x && x.tagName === tagName) > -1;

          return (
            <Button
              key={tagName}
              style={{
                color: `${textColor}`,
                backgroundColor: `${color}`,
              }}
              onClick={
                subTags
                  ? () => handleTagClick(subTags)
                  : () => handleFinalTagClick(tag)
              }
              disabled={hasBeenSelectedAlready}
            >
              <div className="tag">
                <div
                  className={`tagName${
                    tagName.length > 40 ? " isVeryLong" : ""
                  }`}
                >
                  {tagName}
                </div>
                <div>
                  <img
                    height={ICON_DIMENSIONS}
                    width={ICON_DIMENSIONS}
                    alt={`${tagName}`}
                    src={`data:image/svg+xml;base64,${base64data}`}
                  />
                </div>
                {subTags ? (
                  <div className="hasMore">
                    <MoreHorizIcon />
                  </div>
                ) : (
                  <div style={{ padding: "1em" }} />
                )}
              </div>
            </Button>
          );
        })}
      </div>
    </TagsGridStyles>
  );
}
